import React from 'react';
import ReactApexChart from 'react-apexcharts';

interface TopAudienceData {
  success: boolean;
  chartData: {
    categories: string[];
    series: {
      name: string;
      data: number[];
      color: string;
    }[];
    colors: string[];
  };
  audienceInfo: {
    name: string;
    description: string;
    count: string;
  }[];
  maxValue: number;
}

interface AudienceBarChartProps {
  topAudience: TopAudienceData;
}

const AudienceBarChart: React.FC<AudienceBarChartProps> = ({ topAudience }) => {
  if (!topAudience?.success) {
    return (
      <div className="w-full p-6 bg-white border border-gray-200 rounded-xl">
        <h3 className="text-[22px] font-semibold text-black mb-6">Your Top Audiences</h3>
        <p>No audience data available</p>
      </div>
    );
  }

  const options = {
    chart: {
      type: 'bar',
      stacked: true,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 6,
        barHeight: '60%',
      },
    },
    colors: topAudience.chartData.colors,
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: topAudience.chartData.categories,
      position: 'bottom',
      labels: {
        style: {
          colors: '#4A4D4F',
          fontSize: '12px',
          fontFamily: 'Inter',
        },
      },
      axisTicks: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: {
      labels: {
        formatter: function(value: number) {
          if (value >= 1000000) {
            return (value / 1000000).toFixed(0) + 'M';
          }
          if (value >= 1000) {
            return (value / 1000).toFixed(0) + 'K';
          }
          return value;
        },
        style: {
          colors: '#4A4D4F',
          fontSize: '12px',
          fontFamily: 'Inter',
        },
      },
      reversed: false,
      tickAmount: 4,
      min: 0,
      max: topAudience.maxValue,
    },
    grid: {
      borderColor: '#F1F1F1',
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: false,
        },
      },
    },
    legend: {
      show: false,
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: function(value: number) {
          return value.toLocaleString();
        },
      },
    },
  };

  return (
    <div className="w-full p-6 bg-white border border-gray-200 rounded-xl">
      <h3 className="text-[22px] font-semibold text-black mb-6">Your Top Audiences</h3>
      
      <div className="flex flex-wrap gap-6 mb-6">
        {topAudience.audienceInfo.map((audience, index) => (
          <div key={index} className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: topAudience.chartData.colors[index] }}
            ></div>
            <span className="text-sm text-gray-500">{audience.name}</span>
            <span className="text-sm font-semibold">{audience.count}</span>
          </div>
        ))}
      </div>
      
      <div className="mt-2">
        <ReactApexChart 
          options={options} 
          series={topAudience.chartData.series} 
          type="bar" 
          height={350}
        />
      </div>
    </div>
  );
};

export default AudienceBarChart;  